import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Messages } from 'primereact/messages'

import { CheckRequiredFields } from '../../../../components/CheckRequiredFields'
import { Header } from '../../../../components/Header'
import {
  getVisibleNoteParams,
  getVisibleNotes,
} from '../../../../utils/helper/getVisibleNotes'
import { useConfigContext } from '../../../ui-config/context'
import { useInvoiceById } from '../../data'
import { AcceptInvoice, RejectInvoice } from './dialogs'

type Props = {
  invoiceId: string
}

export const Title: React.FC<Props> = ({ invoiceId }) => {
  const navigate = useNavigate()

  const { data: invoice, isLoading } = useInvoiceById(invoiceId)
  const { uiConfig: config } = useConfigContext()

  const [displayRejectInvoice, setDisplayRejectInvoice] = useState(false)
  const [displayAcceptInvoice, setDisplayAcceptInvoice] = useState(false)

  const { t } = useTranslation()
  const messagesRef = useRef<Messages>(null)

  useEffect(() => {
    if (!messagesRef) return

    const messages = getVisibleNotes(invoice?.Payload, 'INVOICE')

    if (!messages) return

    messages.forEach(message => {
      const params = getVisibleNoteParams(message)

      messagesRef.current?.replace({
        id: message.HintId,
        sticky: true,
        severity: 'warn',
        closable: false,
        icon: PrimeIcons.EXCLAMATION_CIRCLE,
        summary: t(`common.visibleNotes.${message.HintId}.summary`),
        detail: t(`common.visibleNotes.${message.HintId}.detail`, params),
      })
    })
  }, [t, messagesRef, invoice?.Payload])

  const actions = (
    <>
      <Button
        label={t('invoice.title.history.label')}
        className="p-button-text"
        onClick={() =>
          navigate(`/orders/history?search=${invoice?.Payload.OrderId}`)
        }
      />

      <Button
        label={t(`common.buttons.reject`)}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={() => setDisplayRejectInvoice(true)}
        visible={config.invoice.rejectInvoice.enabled !== false}
      />

      <CheckRequiredFields data={invoice}>
        {(isDisabled, tooltip) => (
          <>
            {tooltip}
            <Button
              label={t(`common.buttons.send`)}
              icon="pi pi-check"
              className="send-button"
              onClick={() => setDisplayAcceptInvoice(true)}
              visible={config.invoice.acceptInvoice.enabled !== false}
              disabled={isDisabled}
            />
          </>
        )}
      </CheckRequiredFields>

      <RejectInvoice
        visible={displayRejectInvoice}
        setVisible={setDisplayRejectInvoice}
        invoiceId={invoiceId}
      />

      <AcceptInvoice
        visible={displayAcceptInvoice}
        setVisible={setDisplayAcceptInvoice}
        invoiceId={invoiceId}
      />
    </>
  )

  return (
    <>
      <Header
        title={t(`invoice.title.title`, {
          orderId: invoice?.Payload.OrderId,
        })}
        showBackButton={true}
        showPrintButton={true}
        actions={actions}
        isLoading={isLoading}
      />

      <Messages ref={messagesRef} />
    </>
  )
}
